import { Component } from 'react';
import { Link } from 'react-router';
import { Spinner, SpinnerSize } from '@blueprintjs/core';
import { scaleOrdinal, schemeCategory10 } from 'd3';
import { values } from 'lodash';

import { createUsageShortcuts, DefaultCallout, Legend, MainContent, MainPanel, Subhead } from '../../shared';
import { type UsageResponse } from '../api';
import { type ChartTypes, SDKType } from '../model';

import { HighUsageFilter } from './HighUsageFilter';
import { TimeRangeFilter } from './TimeRangeFilter';
import { UsageChart } from './UsageChart';

import '../shared-usage-styles.scss';
import './AccountList.scss';
import './HighAccountUsage.scss';

// Get the time range associated with a label (selectedTimeOption)
type TimeRangeShortcut = {
  label: string;
  dateRange: [number, number];
};

const getTimeRange = (timeOption: string) => {
  const timeRanges = createUsageShortcuts();
  const timeRange = timeRanges.find((sc: object) => {
    const value: TimeRangeShortcut = values(sc)[0];
    return value.label === timeOption;
  });
  if (timeRange !== undefined) {
    const timeRangeValue: TimeRangeShortcut = values(timeRange)[0] as TimeRangeShortcut;
    const [startDate, endDate] = timeRangeValue.dateRange;
    return { to: endDate.toString(), from: startDate.toString() };
  }
  return undefined;
};

export type HighAccountUsageProps = UsageResponse &
  Readonly<{
    isFetching: boolean;
    onFetchHighAccountUsage: (value: SDKType, dates?: { to: string; from: string }) => void;
    chartType: ChartTypes.LINE;
    renderFilters?: () => void;
    tooltipText: string;
    selectedOption?: SDKType;
    limit?: number;
  }>;

type State = {
  readonly selectedOption: SDKType;
  readonly selectedTimeOption: string;
  readonly focusedId?: string;
};

export class HighAccountUsage extends Component<HighAccountUsageProps, State> {
  public state: State = { selectedOption: SDKType.CLIENT, selectedTimeOption: 'Last 14 days' };

  public componentDidMount() {
    this.props.onFetchHighAccountUsage(this.state.selectedOption, getTimeRange(this.state.selectedTimeOption));
  }

  public render() {
    const { isFetching, chartType, tooltipText, limit, series, metadata } = this.props;
    const color = scaleOrdinal<string, string>().range(schemeCategory10);

    return (
      <MainPanel>
        <div className="HighUsage">
          <MainContent>
            <Subhead>Top 10 accounts with high usage</Subhead>
            <div className="usage-filters">
              <HighUsageFilter
                onSelect={(value: SDKType) => this.handleSelect(value)}
                options={[
                  { value: SDKType.SERVER, name: 'Server connections' },
                  { value: SDKType.CLIENT, name: 'Client connections' },
                ]}
                selectedOption={this.state.selectedOption}
                value={SDKType.SERVER}
              />
              <TimeRangeFilter
                onSelect={(value: { to: number; from: number }, label: string) => this.handleTimeSelect(value, label)}
                selectedOption={this.state.selectedTimeOption}
                value="hour"
                timeRange={createUsageShortcuts()}
              />
            </div>
            {series.length === 0 && !isFetching && (
              <div className="UsageGraph--loading HighUsage--no-data">
                <DefaultCallout>There is no usage data.</DefaultCallout>
              </div>
            )}
            {isFetching && (
              <div className="UsageGraph--loading">
                <Spinner size={SpinnerSize.SMALL} />
              </div>
            )}
            {!isFetching && series.length > 0 && (
              <>
                <UsageChart
                  metadata={metadata}
                  limit={limit}
                  series={series}
                  chartType={chartType}
                  tooltipText={tooltipText}
                  color={color}
                  renderToolTip={() => null}
                  focusedSeriesId={this.state.focusedId}
                />
                <div className="Legend">
                  {metadata.map((md, i) => (
                    <Link
                      to={`/accounts/${md._id || ''}`}
                      className="LegendItem"
                      key={i}
                      onMouseEnter={() => {
                        this.setState({ focusedId: md._id });
                      }}
                      onMouseLeave={() => {
                        this.setState({ focusedId: undefined });
                      }}
                    >
                      <Legend stroke={color(i.toString())} text={md.organization || md.ownerEmail || 'Unknown'} />
                    </Link>
                  ))}
                </div>
              </>
            )}
          </MainContent>
        </div>
      </MainPanel>
    );
  }

  private handleTimeSelect = (value: { to: number; from: number }, label: string) => {
    this.setState({ selectedTimeOption: label });
    this.props.onFetchHighAccountUsage(this.state.selectedOption, {
      to: value.to.toString(),
      from: value.from.toString(),
    });
  };

  private handleSelect = (value: SDKType) => {
    this.setState({ selectedOption: value });
    this.props.onFetchHighAccountUsage(value, getTimeRange(this.state.selectedTimeOption));
  };
}
